"use client";

import React, { useState } from "react";
import Link from "next/link";

import { useForm } from "react-hook-form";
import { Check, X } from "lucide-react";
import { userService } from "@/services/user.service";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { checkIsLogged } from "../../utlis/authCheck";
import { usePWA } from "@/utlis/usePWA";
import {
	IconMail,
	IconLock,
	IconEyeOpen,
	IconEyeOff,
	IconClose,
	IconArrowLeft,
} from "./AuthIcons";

function PasswordRule({ ok, children }) {
	return (
		<li className='reset-rule' style={{ color: ok ? "#16a34a" : "#8a96a6" }}>
			{ok ? <Check size={14} /> : <X size={14} />}
			<span>{children}</span>
		</li>
	);
}

export default function Login() {
	checkIsLogged("/");

	const isPWA = usePWA();

	const [view, setView] = useState("login");
	const [showPassword, setShowPassword] = useState(false);
	const [recoverPending, setRecoverPending] = useState(false);
	const [recoverEmail, setRecoverEmail] = useState("");
	const [codePending, setCodePending] = useState(false);
	const [showCodePassword, setShowCodePassword] = useState(false);
	const [showCodeConfirm, setShowCodeConfirm] = useState(false);

	const {
		register: registerLogin,
		handleSubmit: handleSubmitLogin,
		formState: { errors: errorsLogin },
	} = useForm();

	const {
		register: registerRecover,
		handleSubmit: handleSubmitRecover,
		formState: { errors: errorsRecover },
	} = useForm();

	const {
		register: registerCode,
		handleSubmit: handleSubmitCode,
		watch: watchCode,
		formState: { errors: errorsCode },
	} = useForm();

	const codeNewPassword = watchCode("newPassword", "");
	const codeRules = {
		length: codeNewPassword.length >= 8,
		digit: /\d/.test(codeNewPassword),
	};

	const onSubmit = async (data) => {
		const userEmail = data.email;
		const userPass = data.password;

		try {
			await userService.login(userEmail, userPass);
			window.location.href = "/";
		} catch (error) {
			toast.error(error);
		}
	};

	const onRecover = async (data) => {
		if (recoverPending) return;

		setRecoverPending(true);
		try {
			await userService.forgotPassword(data.email, { viaCode: isPWA });
			setRecoverEmail(data.email);
			setView(isPWA ? "recover-code" : "recover-sent");
		} catch (error) {
			toast.error("A apărut o eroare. Încearcă din nou.");
		} finally {
			setRecoverPending(false);
		}
	};

	const onSubmitCode = async (data) => {
		if (codePending) return;

		setCodePending(true);
		try {
			await userService.resetPasswordWithCode({
				email: recoverEmail,
				code: data.code,
				newPassword: data.newPassword,
				passwordConfirm: data.passwordConfirm,
			});
			window.location.href = "/";
		} catch (error) {
			toast.error(typeof error === "string" ? error : "A apărut o eroare. Încearcă din nou.");
			setCodePending(false);
		}
	};

	return (
		<>
			<style jsx>{`
				/* =========================================================
				   PAGE
				========================================================= */

				.auth-page {
					position: relative;
					width: 100%;
					min-height: 100vh;

					display: flex;
					justify-content: center;
					align-items: flex-start;

					padding: 70px 20px 60px;

					background: #ffffff;

					box-sizing: border-box;
				}

				/* =========================================================
				   CLOSE BUTTON
				========================================================= */

				.auth-close {
					position: fixed;

					top: 24px;
					right: 28px;

					width: 54px;
					height: 54px;

					display: flex;
					align-items: center;
					justify-content: center;

					border-radius: 50%;

					background: #f7f8fa;

					color: #1f2937;

					text-decoration: none;

					box-shadow:
						0 4px 14px rgba(15, 23, 42, 0.06),
						0 1px 3px rgba(15, 23, 42, 0.04);

					z-index: 20;

					transition:
						transform 0.15s ease,
						background 0.15s ease;
				}

				.auth-close:hover {
					background: #f1f3f6;
				}

				.auth-close:active {
					transform: scale(0.94);
				}

				.auth-back {
					position: fixed;

					top: 24px;
					left: 28px;

					width: 54px;
					height: 54px;

					display: flex;
					align-items: center;
					justify-content: center;

					border: 0;
					border-radius: 50%;

					background: #f7f8fa;

					color: #1f2937;

					cursor: pointer;

					box-shadow:
						0 4px 14px rgba(15, 23, 42, 0.06),
						0 1px 3px rgba(15, 23, 42, 0.04);

					z-index: 20;

					transition:
						transform 0.15s ease,
						background 0.15s ease;
				}

				.auth-back:hover {
					background: #f1f3f6;
				}

				.auth-back:active {
					transform: scale(0.94);
				}

				/* =========================================================
				   MAIN CONTENT
				========================================================= */

				.auth-card {
					width: 100%;
					max-width: 620px;

					display: flex;
					flex-direction: column;
					align-items: center;

					box-sizing: border-box;
				}

				/* =========================================================
				   LOGO
				========================================================= */

				.auth-logo-wrap {
					display: flex;
					align-items: center;
					justify-content: center;

					margin-bottom: 8px;
				}

				.auth-logo {
					display: block;

					width: 210px;
					max-width: 70vw;
					height: auto;

					object-fit: contain;
				}

				.auth-tagline {
					margin: 0 0 42px;

					color: #3b82f6;

					font-size: 21px;
					font-weight: 600;

					line-height: 1.3;

					text-align: center;
				}

				/* =========================================================
				   FORMS
				========================================================= */

				.auth-card form {
					width: 100%;
					margin: 0;
					padding: 0;
				}

				/* =========================================================
				   INPUT FIELD
				========================================================= */

				.auth-field {
					position: relative;

					width: 100%;
					height: 66px;

					margin-bottom: 16px;

					display: flex;
					align-items: center;

					box-sizing: border-box;

					background: #ffffff;

					border: 1px solid #d9dde4;

					border-radius: 34px;

					overflow: hidden;

					transition:
						border-color 0.2s ease,
						box-shadow 0.2s ease;
				}

				/*
				 * IMPORTANT:
				 * iconița este în interiorul aceluiași container.
				 * Nu există border separat pentru icon.
				 */

				.auth-field:focus-within {
					border-color: #3b82f6;

					box-shadow:
						0 0 0 3px rgba(59, 130, 246, 0.10);
				}

				/* =========================================================
				   LEFT ICON
				========================================================= */

				.auth-field-icon {
					position: absolute;

					left: 22px;
					top: 50%;

					width: 21px;
					height: 21px;

					transform: translateY(-50%);

					display: flex;
					align-items: center;
					justify-content: center;

					color: #6b7280;

					pointer-events: none;

					z-index: 2;
				}

				.auth-field-icon :global(svg) {
					width: 21px !important;
					height: 21px !important;

					display: block;
				}

				/* =========================================================
				   INPUT
				========================================================= */

				.auth-input {
					width: 100% !important;
					height: 100% !important;

					margin: 0 !important;

					padding: 0 58px 0 58px !important;

					border: 0 !important;
					outline: 0 !important;

					border-radius: 34px !important;

					background: transparent !important;

					box-shadow: none !important;

					color: #374151 !important;

					font-family: inherit;
					font-size: 17px;
					font-weight: 400;

					line-height: normal;

					box-sizing: border-box;

					-webkit-appearance: none;
					appearance: none;
				}

				.auth-input::placeholder {
					color: #8a96a6 !important;

					opacity: 1;
				}

				.auth-input:focus {
					border: 0 !important;
					outline: 0 !important;
					box-shadow: none !important;
				}

				/* elimină stilurile browserului */

				.auth-input:-webkit-autofill,
				.auth-input:-webkit-autofill:hover,
				.auth-input:-webkit-autofill:focus {
					-webkit-text-fill-color: #374151 !important;

					-webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;

					transition: background-color 9999s ease-in-out 0s;
				}

				/* =========================================================
				   EYE BUTTON
				========================================================= */

				.auth-eye-btn {
					position: absolute;

					right: 17px;
					top: 50%;

					width: 42px;
					height: 42px;

					transform: translateY(-50%);

					display: flex;
					align-items: center;
					justify-content: center;

					padding: 0;

					border: 0 !important;
					outline: none !important;

					border-radius: 50%;

					background: transparent !important;

					color: #8a96a6;

					cursor: pointer;

					z-index: 3;

					box-shadow: none !important;

					transition:
						background 0.15s ease,
						color 0.15s ease;
				}

				.auth-eye-btn:hover {
					background: #f3f4f6 !important;
					color: #4b5563;
				}

				.auth-eye-btn:focus,
				.auth-eye-btn:focus-visible {
					outline: none !important;
					box-shadow: none !important;
				}

				.auth-eye-btn :global(svg) {
					width: 20px !important;
					height: 20px !important;

					display: block;
				}

				.auth-eye-btn.is-active {
					color: #3b82f6;
				}

				/* =========================================================
				   ERROR
				========================================================= */

				.auth-error {
					display: block;

					margin: -7px 0 12px 22px;

					color: #dc2626;

					font-size: 13px;
					font-weight: 500;

					line-height: 1.3;
				}

				/* =========================================================
				   PRIMARY BUTTON
				========================================================= */

				.auth-btn-primary {
					width: 100%;
					height: 66px;

					margin-top: 14px;

					display: flex;
					align-items: center;
					justify-content: center;

					border: 0;
					border-radius: 34px;

					background: #3b82f6;
					color: #ffffff;

					font-family: inherit;

					font-size: 18px;
					font-weight: 700;

					cursor: pointer;

					box-shadow: none;

					transition:
						background 0.18s ease,
						transform 0.15s ease,
						box-shadow 0.18s ease;
				}

				.auth-btn-primary:hover {
					background: #2563eb;

					box-shadow:
						0 6px 18px rgba(59, 130, 246, 0.18);
				}

				.auth-btn-primary:active {
					transform: scale(0.985);
				}

				.auth-btn-primary:focus,
				.auth-btn-primary:focus-visible {
					outline: none;
				}

				/* =========================================================
				   SECONDARY BUTTONS
				========================================================= */

				.auth-btn-ghost {
					width: 100%;
					min-height: 62px;

					margin-top: 12px;

					display: flex;
					align-items: center;
					justify-content: center;

					padding: 14px 20px;

					border: 0;
					border-radius: 32px;

					background: #f5f6f8;

					color: #29313d;

					font-family: inherit;

					font-size: 17px;
					font-weight: 600;

					line-height: 1.2;

					text-align: center;
					text-decoration: none;

					cursor: pointer;

					box-sizing: border-box;

					transition:
						background 0.18s ease,
						transform 0.15s ease;
				}

				.auth-btn-ghost:hover {
					background: #eef0f3;
				}

				.auth-btn-ghost:active {
					transform: scale(0.985);
				}

				/* =========================================================
				   RESET RULES
				========================================================= */

				.reset-rules {
					list-style: none;

					margin: -6px 0 16px 22px;
					padding: 0;

					display: flex;
					gap: 18px;
				}

				.reset-rule {
					display: flex;
					align-items: center;
					gap: 6px;

					font-size: 13px;
					font-weight: 500;
				}

				/* =========================================================
				   RECOVER
				========================================================= */

				.auth-heading {
					margin: 0 0 10px;

					color: #202733;

					font-size: 30px;
					font-weight: 700;

					line-height: 1.2;

					text-align: center;
				}

				.auth-subtext {
					max-width: 480px;

					margin: 0 0 30px;

					color: #6b7280;

					font-size: 16px;
					line-height: 1.5;

					text-align: center;
				}

				/* =========================================================
				   TABLET
				========================================================= */

				@media (max-width: 700px) {
					.auth-page {
						padding: 55px 22px 45px;
					}

					.auth-close {
						top: 18px;
						right: 18px;

						width: 48px;
						height: 48px;
					}

					.auth-back {
						top: 18px;
						left: 18px;

						width: 48px;
						height: 48px;
					}

					.auth-logo {
						width: 190px;
					}

					.auth-tagline {
						margin-bottom: 32px;

						font-size: 19px;
					}

					.auth-field {
						height: 62px;
					}

					.auth-btn-primary {
						height: 62px;
					}
				}

				/* =========================================================
				   MOBILE
				========================================================= */

				@media (max-width: 480px) {
					.auth-page {
						min-height: 100svh;

						padding: 72px 18px 35px;
					}

					.auth-card {
						max-width: none;
					}

					.auth-logo {
						width: 170px;
					}

					.auth-tagline {
						margin-bottom: 30px;

						font-size: 17px;
					}

					.auth-field {
						height: 60px;

						margin-bottom: 13px;

						border-radius: 30px;
					}

					.auth-field-icon {
						left: 19px;

						width: 19px;
						height: 19px;
					}

					.auth-field-icon :global(svg) {
						width: 19px !important;
						height: 19px !important;
					}

					.auth-input {
						padding-left: 52px !important;
						padding-right: 52px !important;

						font-size: 16px;
					}

					.auth-eye-btn {
						right: 12px;
					}

					.auth-btn-primary {
						height: 60px;

						margin-top: 12px;

						border-radius: 30px;

						font-size: 17px;
					}

					.auth-btn-ghost {
						min-height: 58px;

						border-radius: 29px;

						font-size: 16px;
					}

					.auth-heading {
						font-size: 26px;
					}

					.auth-subtext {
						font-size: 15px;
					}
				}

				/* =========================================================
				   VERY SMALL PHONES
				========================================================= */

				@media (max-width: 360px) {
					.auth-page {
						padding-left: 14px;
						padding-right: 14px;
					}

					.auth-logo {
						width: 155px;
					}

					.auth-field {
						height: 56px;
					}

					.auth-input {
						font-size: 15px;
					}

					.auth-btn-primary {
						height: 56px;
					}

					.auth-btn-ghost {
						min-height: 54px;
					}
				}
			`}</style>

			<div className='auth-page'>
				{(view === "recover" || view === "recover-sent" || view === "recover-code") && (
					<button
						type='button'
						className='auth-topbtn auth-back'
						aria-label='Înapoi'
						onClick={() => setView("login")}>
						<IconArrowLeft />
					</button>
				)}

				<Link
					href='/'
					className='auth-topbtn auth-close'
					aria-label='Închide'>
					<IconClose />
				</Link>

				<div className='auth-card'>
					<div className='auth-logo-wrap'>
						<img
							src='/images/password.webp'
							alt='eMAGYL'
							className='auth-logo'
						/>
					</div>

					<p className='auth-tagline'>
						Un cont, mai multe beneficii!
					</p>

					{view === "login" ? (
						<>
							<form onSubmit={handleSubmitLogin(onSubmit)}>
								{/* EMAIL */}
								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconMail />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Introdu adresa de email'
										type='email'
										autoComplete='email'
										{...registerLogin("email", {
											required: true,
											pattern: /^\S+@\S+$/i,
										})}
									/>
								</div>

								{errorsLogin.email && (
									<span className='auth-error'>
										Introdu o adresă de email validă.
									</span>
								)}

								{/* PASSWORD */}
								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconLock />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Introdu parola'
										type={
											showPassword
												? "text"
												: "password"
										}
										autoComplete='current-password'
										{...registerLogin("password", {
											required: true,
										})}
									/>

									<button
										type='button'
										className={`auth-eye-btn${
											showPassword
												? " is-active"
												: ""
										}`}
										onClick={() =>
											setShowPassword(
												(v) => !v
											)
										}
										aria-label={
											showPassword
												? "Ascunde parola"
												: "Arată parola"
										}>
										{showPassword ? (
											<IconEyeOff />
										) : (
											<IconEyeOpen />
										)}
									</button>
								</div>

								<button
									type='submit'
									className='auth-btn-primary'>
									Continuă
								</button>
							</form>

							<button
								type='button'
								className='auth-btn-ghost'
								onClick={() => setView("recover")}>
								Ai uitat parola?
							</button>

							<button
								type='button'
								className='auth-btn-ghost'
								onClick={() => window.location.href = "/inregistrare"}>
								Creează un cont nou
							</button>
						</>
					) : view === "recover" ? (
						<>
							<h1 className='auth-heading'>
								Resetează parola
							</h1>

							<p className='auth-subtext'>
								Îți trimitem un email pentru resetarea
								parolei.
							</p>

							<form
								onSubmit={handleSubmitRecover(
									onRecover
								)}>
								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconMail />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Introdu adresa de email'
										type='email'
										autoComplete='email'
										{...registerRecover("email", {
											required: true,
											pattern: /^\S+@\S+$/i,
										})}
									/>
								</div>

								{errorsRecover.email && (
									<span className='auth-error'>
										Introdu o adresă de email validă.
									</span>
								)}

								<button
									type='submit'
									className='auth-btn-primary'
									disabled={recoverPending}>
									{recoverPending
										? "Se trimite..."
										: "Trimite email"}
								</button>
							</form>

							<button
								type='button'
								className='auth-btn-ghost'
								onClick={() => setView("login")}>
								Anulează
							</button>
						</>
					) : view === "recover-code" ? (
						<>
							<h1 className='auth-heading'>
								Introdu codul primit
							</h1>

							<p className='auth-subtext'>
								Am trimis un cod de 6 cifre pe {recoverEmail}.
								Introdu-l mai jos împreună cu noua parolă.
							</p>

							<form onSubmit={handleSubmitCode(onSubmitCode)}>
								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconLock />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Cod din 6 cifre'
										type='text'
										inputMode='numeric'
										maxLength={6}
										autoComplete='one-time-code'
										{...registerCode("code", {
											required: true,
											pattern: /^\d{6}$/,
										})}
									/>
								</div>

								{errorsCode.code && (
									<span className='auth-error'>
										Codul trebuie să aibă 6 cifre.
									</span>
								)}

								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconLock />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Parolă nouă'
										type={showCodePassword ? "text" : "password"}
										autoComplete='new-password'
										{...registerCode("newPassword", {
											required: true,
											minLength: 8,
											validate: (v) => /\d/.test(v),
										})}
									/>

									<button
										type='button'
										className={`auth-eye-btn${showCodePassword ? " is-active" : ""}`}
										onClick={() => setShowCodePassword((v) => !v)}
										aria-label={showCodePassword ? "Ascunde parola" : "Arată parola"}>
										{showCodePassword ? <IconEyeOff /> : <IconEyeOpen />}
									</button>
								</div>

								<ul className='reset-rules'>
									<PasswordRule ok={codeRules.length}>minimum 8 caractere</PasswordRule>
									<PasswordRule ok={codeRules.digit}>cel puțin o cifră</PasswordRule>
								</ul>

								<div className='auth-field'>
									<span className='auth-field-icon'>
										<IconLock />
									</span>

									<input
										required
										className='auth-input'
										placeholder='Confirmă parola nouă'
										type={showCodeConfirm ? "text" : "password"}
										autoComplete='new-password'
										{...registerCode("passwordConfirm", {
											required: true,
											validate: (v) => v === codeNewPassword,
										})}
									/>

									<button
										type='button'
										className={`auth-eye-btn${showCodeConfirm ? " is-active" : ""}`}
										onClick={() => setShowCodeConfirm((v) => !v)}
										aria-label={showCodeConfirm ? "Ascunde parola" : "Arată parola"}>
										{showCodeConfirm ? <IconEyeOff /> : <IconEyeOpen />}
									</button>
								</div>

								{errorsCode.passwordConfirm && (
									<span className='auth-error'>
										Parolele introduse nu coincid.
									</span>
								)}

								<button
									type='submit'
									className='auth-btn-primary'
									disabled={codePending}>
									{codePending ? "Se salvează..." : "Salvează parola"}
								</button>
							</form>

							<button
								type='button'
								className='auth-btn-ghost'
								onClick={() => setView("login")}>
								Anulează
							</button>
						</>
					) : (
						<>
							<h1 className='auth-heading'>
								Verifică-ți emailul
							</h1>

							<p className='auth-subtext'>
								Dacă adresa introdusă este asociată unui
								cont eMagyl, vei primi în câteva minute un
								email cu un link pentru resetarea parolei.
								Linkul este valabil 30 de minute.
							</p>

							<button
								type='button'
								className='auth-btn-ghost'
								onClick={() => setView("login")}>
								Înapoi la autentificare
							</button>
						</>
					)}
				</div>
			</div>
		</>
	);
}